import { useCallback } from 'react'
import { heapEvent } from '@equipmentshare/heaper'
import {
	CountryCode,
	FulfillmentType,
	useCreateFulfillmentOrderMutation,
	useUpdateDemandRequestLineItemFulfillerNotesMutation,
} from '../../../graphql'
import { CreateFulfillmentOrderModalSubmitParams } from '../components/CreateFulfillmentOrderModal'

type Inventory = {
	id: string,
}

type DemandRequest = {
	id: string,
	// The fulfilling inventory, or the main DC when a routed stock request has none
	owningInventory: Inventory,
	requestingInventory: Inventory,
}

type UseFulfillmentOrderCreationParams = {
	request: DemandRequest | undefined | null,
	addToast: ( message: string, color?: 'success' | 'danger' | 'warning' | 'primary' ) => void,
	clearSelection: () => void,
}

export type SubmitFulfillmentOrderResult = {
	success: boolean,
}

export function useFulfillmentOrderCreation( {
	request,
	addToast,
	clearSelection,
}: UseFulfillmentOrderCreationParams ) {
	const [ createFulfillmentOrder, { loading: isSubmitting } ] = useCreateFulfillmentOrderMutation( {
		// The double-submission gate reads inFlightFulfillmentOrderCount from this query, so the
		// refetch must complete before we treat the submit as done — otherwise the gate is stale
		// until a manual reload (and a failed refetch would go unnoticed).
		refetchQueries: [ 'DemandRequest' ],
		awaitRefetchQueries: true,
	} )
	const [ updateFulfillerNotes ] = useUpdateDemandRequestLineItemFulfillerNotesMutation()

	const submit = useCallback( async(
		params: CreateFulfillmentOrderModalSubmitParams,
	): Promise<SubmitFulfillmentOrderResult> => {
		if ( !request || params.items.length === 0 ) {
			return { success: false }
		}

		const lineItems = params.items.map( ( row ) => ( {
			productId: row.substitute ? row.substitute.part.id.toString() : row.lineItem.productId,
			quantity: row.quantity,
			demandRequestLineItemId: row.lineItem.lineItemId,
		} ) )

		const { shipTo, fulfillmentType, includeShipTo } = params
		const isMttLoadout = fulfillmentType === FulfillmentType.MttLoadout

		// The ship-to address and contact phone are collected (and may be prefilled
		// from the branch) for every fulfillment type, but MTT loadouts only submit
		// them when the costentoryMttAddressForm flag is on. The modal folds that
		// rule into params.includeShipTo, so honor it here rather than re-deriving
		// the flag — otherwise a prefilled branch address would silently ship on an
		// MTT loadout. When the address is sent, include it only if a recipient or
		// street field is set; country alone (which always defaults to US) does not
		// count — otherwise every order would carry a country-only address and trip
		// the inventory-api recipient requirement.
		const hasShippingAddress = includeShipTo && !!(
			shipTo.name || shipTo.companyName || shipTo.line1 || shipTo.line2 || shipTo.city || shipTo.state || shipTo.postalCode
		)
		const shippingAddress = hasShippingAddress
			? {
				name: shipTo.name || undefined,
				companyName: shipTo.companyName || undefined,
				line1: shipTo.line1 || undefined,
				line2: shipTo.line2 || undefined,
				city: shipTo.city || undefined,
				state: shipTo.state || undefined,
				postalCode: shipTo.postalCode || undefined,
				// shipTo.country holds a country code string ('US'); the input expects
				// the CountryCode enum.
				country: ( shipTo.country ? ( shipTo.country as CountryCode ) : undefined ) as CountryCode | undefined,
			}
			: undefined

		// INVENTORY_TRANSFER fulfills a demand request into the requesting
		// inventory; MTT_LOADOUT loads the requesting MTT (also an inventory
		// location). Both require a destination. DIRECT_SHIPMENT ships to a
		// customer address and has no destination inventory.
		const destinationInventoryId = fulfillmentType === FulfillmentType.DirectShipment
			? undefined
			: request.requestingInventory.id

		try {
			const result = await createFulfillmentOrder( {
				variables: {
					input: {
						// Chosen in the form (defaulting to the request's owning
						// inventory), not taken from the request — a stock request raised
						// against one DC can be fulfilled from another, which is how
						// mini-DC and Will-Call orders get created.
						fulfillingInventoryId: params.fulfillingInventoryId || request.owningInventory.id,
						destinationInventoryId,
						contactPhone: includeShipTo ? ( shipTo.contactPhone || undefined ) : undefined,
						shippingAddress,
						lineItems,
						fulfillmentType,
						mttId: isMttLoadout ? ( params.mttId || undefined ) : undefined,
						mttQuadrant: isMttLoadout ? ( params.mttQuadrant || undefined ) : undefined,
						// dateNeededBy is a DATE; the modal already serializes to 'YYYY-MM-DD'.
						dateNeededBy: params.dateNeededBy || undefined,
					},
				},
			} )

			const orderResult = result.data?.createFulfillmentOrder
			if ( orderResult?.success && orderResult.fulfillmentOrder ) {
				heapEvent( 'Inventory - Stock Request - Bulk Actions - Create Fulfillment Order', {
					demand_request_id: request.id,
					item_count: params.items.length,
				} )

				params.items.forEach( ( row ) => {
					if ( row.substitute ) {
						heapEvent( 'Inventory - Stock Request - Bulk Actions - Substitute Selected', {
							demand_request_id: request.id,
							line_item_id: row.lineItem.lineItemId,
							original_product_id: row.lineItem.productId,
							substitute_product_id: row.substitute.part.id.toString(),
						} )
					}
				} )

				if ( params.fulfillingInventoryId && params.fulfillingInventoryId !== request.owningInventory.id ) {
					heapEvent( 'Inventory - Stock Request - Bulk Actions - Fulfilling DC Overridden', {
						demand_request_id: request.id,
						request_fulfilling_inventory_id: request.owningInventory.id,
						chosen_fulfilling_inventory_id: params.fulfillingInventoryId,
					} )
				}

				if ( params.overriddenShipToFields.length > 0 ) {
					heapEvent( 'Inventory - Stock Request - Bulk Actions - Ship-to Overridden', {
						demand_request_id: request.id,
						overridden_fields: params.overriddenShipToFields.join( ',' ),
					} )
				}

				addToast( `Fulfillment order FO-${orderResult.fulfillmentOrder.orderNumber} created` )

				if ( params.fulfillerNotesUpdates.length > 0 ) {
					const noteResults = await Promise.all( params.fulfillerNotesUpdates.map( async( update ) => {
						try {
							const noteResult = await updateFulfillerNotes( {
								variables: {
									input: {
										demandRequestId: request.id,
										lineItemId: update.lineItemId,
										fulfillerNotes: update.fulfillerNotes.trim() || null,
									},
								},
							} )
							return noteResult.data?.updateDemandRequestLineItemFulfillerNotes?.success ?? false
						} catch {
							return false
						}
					} ) )

					const failedCount = noteResults.filter( ( success ) => !success ).length
					if ( failedCount > 0 ) {
						addToast(
							`Fulfillment order created, but ${failedCount} ${failedCount === 1 ? 'note' : 'notes'} failed to save`,
							'warning',
						)
					}
				}

				clearSelection()
				return { success: true }
			}

			addToast( `Failed to create fulfillment order: ${orderResult?.message ?? 'Unknown error'}`, 'danger' )
			return { success: false }
		} catch ( err ) {
			addToast( `Failed to create fulfillment order: ${err instanceof Error ? err.message : 'Unknown error'}`, 'danger' )
			return { success: false }
		}
	}, [ request, createFulfillmentOrder, updateFulfillerNotes, addToast, clearSelection ] )

	return {
		submit,
		isSubmitting,
	}
}
